// Renderers for each boxed-expression type. They share a common signature
//
//   render(expr, ctx) -> HTMLElement
//
// where `ctx` carries the moddle factory, the onChange callback, and the
// dispatcher function so a view can recurse into a nested expression.
// All mutations happen directly on the moddle tree; onChange is informational
// (callers may use it to mark the document dirty).

import {
  makeEditable,
  createExpression,
  createContextEntry,
  createInformationItem,
  createBinding,
  textOf,
  getExpressionTypeLabel,
} from './shared';
import { makeFeelEditable } from './feelField';
import { FEEL_TYPES } from '../widgets/feelTypes';
import { sanitizeFeelIdent, bindNoSpaces } from '../feel/ident';

// Chip + popover para editar el typeRef de un InformationItem (variable / parameter).
// Estilizado para encajar al lado del nombre, sin desplazar el layout existente.
function buildTypeRefChip(currentType, onPick) {
  const chip = document.createElement('span');
  chip.className = 'epx-dmn-expr__type-chip';
  chip.title = 'Data type (typeRef) — click to change';
  chip.textContent = ': ' + (currentType || 'string');
  chip.addEventListener('click', (evt) => {
    evt.stopPropagation();
    document.querySelectorAll('.epx-dmn-expr__type-popover').forEach((p) => p.remove());

    const menu = document.createElement('div');
    menu.className = 'epx-dmn-expr__type-popover';
    const rect = evt.currentTarget.getBoundingClientRect();
    menu.style.left = rect.left + 'px';
    menu.style.top = (rect.bottom + 2) + 'px';
    FEEL_TYPES.forEach((t) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = t;
      if ((currentType || 'string') === t) b.classList.add('is-active');
      b.addEventListener('click', () => {
        onPick(t);
        menu.remove();
      });
      menu.appendChild(b);
    });
    document.body.appendChild(menu);
    setTimeout(() => {
      const offClick = (e) => {
        if (!menu.contains(e.target)) {
          menu.remove();
          document.removeEventListener('click', offClick, true);
        }
      };
      document.addEventListener('click', offClick, true);
    }, 0);
  });
  return chip;
}

// ──────────────────────────────────────────────────────────────────────────
// LiteralExpression — a single editable text cell.
// ──────────────────────────────────────────────────────────────────────────
export function renderLiteral(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--literal';
  box.appendChild(makeFeelEditable(
    textOf(expr),
    'expression',
    (v) => { expr.text = v; ctx.onChange(); },
    ctx.getScope,
  ));
  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// Context — rows of (name, nested expression). An optional trailing entry
// without a name is the "result" entry (FEEL's boxed-context convention).
// ──────────────────────────────────────────────────────────────────────────
export function renderContext(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--context';

  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid';

  if (!expr.contextEntry) expr.contextEntry = [];

  const baseGetScope = typeof ctx.getScope === 'function' ? ctx.getScope : () => [];

  // The last entry without a name is the "result" entry by FEEL convention —
  // its value becomes the value of the whole context.
  const lastIdx = expr.contextEntry.length - 1;
  const resultIdx = expr.contextEntry[lastIdx] && !(expr.contextEntry[lastIdx].variable && expr.contextEntry[lastIdx].variable.name)
    ? lastIdx
    : -1;

  expr.contextEntry.forEach((entry, idx) => {
    const tr = document.createElement('tr');

    // Earlier sibling entries are in scope for this entry's value (FEEL
    // boxed-context semantics). Surface them so the inline FEEL highlighter
    // can recognize sibling references instead of painting them red.
    const siblingScope = [];
    for (let i = 0; i < idx; i++) {
      const sib = expr.contextEntry[i];
      const sibName = sib && sib.variable && sib.variable.name;
      if (!sibName) continue;
      siblingScope.push({
        name: sibName,
        typeRef: (sib.variable && sib.variable.typeRef) || null,
        sourceId: null,
        sourceName: 'Context entry',
        sourceKind: 'ContextEntry',
      });
    }
    const entryCtx = siblingScope.length
      ? { ...ctx, getScope: () => [...baseGetScope(), ...siblingScope] }
      : ctx;

    const nameCell = document.createElement('td');
    nameCell.className = 'epx-dmn-expr__name';
    const hasVar = !!entry.variable;
    const isResultEntry = idx === resultIdx;
    const editable = bindNoSpaces(makeEditable(
      hasVar ? entry.variable.name || '' : '',
      isResultEntry ? '(result)' : 'name',
      (raw) => {
        const v = sanitizeFeelIdent(raw);
        if (!v) {
          entry.variable = undefined; // becomes the result entry
        } else {
          if (!entry.variable) entry.variable = createInformationItem(ctx.moddle, { name: v });
          else entry.variable.name = v;
        }
        ctx.onChange();
      }
    ));
    if (isResultEntry) {
      editable.classList.add('epx-dmn-expr__name--result');
      editable.title = 'This entry has no name, so its value is the result of the whole Context. Type a name to make it a regular entry.';
    }
    nameCell.appendChild(editable);
    if (hasVar) {
      const currentTypeRef = entry.variable.typeRef || 'string';
      nameCell.appendChild(buildTypeRefChip(currentTypeRef, (newType) => {
        entry.variable.typeRef = newType;
        ctx.onChange();
      }));
    }
    tr.appendChild(nameCell);

    const valueCell = document.createElement('td');
    valueCell.className = 'epx-dmn-expr__cell';
    if (!entry.value) entry.value = createExpression(ctx.moddle, 'dmn:LiteralExpression');
    const valueBox = ctx.render(entry.value, entryCtx);
    valueCell.appendChild(valueBox);
    tr.appendChild(valueCell);

    const typeCell = document.createElement('td');
    typeCell.className = 'epx-dmn-expr__type';
    typeCell.appendChild(buildTypePicker(entry.value, ctx, (newExpr) => {
      entry.value = newExpr;
      ctx.onChange();
    }));
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove entry';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.contextEntry.splice(idx, 1);
      ctx.onChange();
    });
    typeCell.appendChild(rm);
    tr.appendChild(typeCell);

    table.appendChild(tr);
  });

  const addTr = document.createElement('tr');
  const addTd = document.createElement('td');
  addTd.colSpan = 3;
  addTd.className = 'epx-dmn-expr__add-row';
  addTd.textContent = '+ Add context entry';
  addTd.addEventListener('click', () => {
    expr.contextEntry.push(createContextEntry(ctx.moddle, { name: '' }));
    ctx.onChange();
  });
  addTr.appendChild(addTd);
  table.appendChild(addTr);

  box.appendChild(table);
  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// List — a vertical list of nested expressions.
// ──────────────────────────────────────────────────────────────────────────
export function renderList(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--list';

  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid';

  if (!expr.elements) expr.elements = [];
  expr.elements.forEach((child, idx) => {
    const tr = document.createElement('tr');

    const idxCell = document.createElement('td');
    idxCell.className = 'epx-dmn-expr__index';
    idxCell.textContent = String(idx + 1);
    tr.appendChild(idxCell);

    const valueCell = document.createElement('td');
    valueCell.className = 'epx-dmn-expr__cell';
    valueCell.appendChild(ctx.render(child, ctx));
    tr.appendChild(valueCell);

    const typeCell = document.createElement('td');
    typeCell.className = 'epx-dmn-expr__type';
    typeCell.appendChild(buildTypePicker(child, ctx, (newExpr) => {
      expr.elements[idx] = newExpr;
      ctx.onChange();
    }));
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove item';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.elements.splice(idx, 1);
      ctx.onChange();
    });
    typeCell.appendChild(rm);
    tr.appendChild(typeCell);

    table.appendChild(tr);
  });

  const addTr = document.createElement('tr');
  const addTd = document.createElement('td');
  addTd.colSpan = 3;
  addTd.className = 'epx-dmn-expr__add-row';
  addTd.textContent = '+ Add list item';
  addTd.addEventListener('click', () => {
    expr.elements.push(createExpression(ctx.moddle, 'dmn:LiteralExpression'));
    ctx.onChange();
  });
  addTr.appendChild(addTd);
  table.appendChild(addTr);

  box.appendChild(table);
  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// Invocation — header (called function) + binding rows.
// ──────────────────────────────────────────────────────────────────────────
export function renderInvocation(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--invocation';

  // Header: called function (typically a literal referring to a BKM name)
  const header = document.createElement('div');
  header.className = 'epx-dmn-expr__invocation-header';
  if (!expr.calledFunction) {
    expr.calledFunction = createExpression(ctx.moddle, 'dmn:LiteralExpression');
  }
  header.appendChild(makeFeelEditable(
    textOf(expr.calledFunction),
    'BKM name',
    (v) => { expr.calledFunction.text = v; ctx.onChange(); },
    ctx.getScope,
    'epx-dmn-expr__invocation-name',
  ));
  box.appendChild(header);

  // Bindings
  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid';
  if (!expr.binding) expr.binding = [];
  expr.binding.forEach((bind, idx) => {
    const tr = document.createElement('tr');

    const nameCell = document.createElement('td');
    nameCell.className = 'epx-dmn-expr__name';
    if (!bind.parameter) bind.parameter = createInformationItem(ctx.moddle);
    nameCell.appendChild(makeEditable(bind.parameter.name || '', 'parameter', (v) => {
      bind.parameter.name = v;
      ctx.onChange();
    }));
    nameCell.appendChild(buildTypeRefChip(bind.parameter.typeRef || 'string', (newType) => {
      bind.parameter.typeRef = newType;
      ctx.onChange();
    }));
    tr.appendChild(nameCell);

    const valueCell = document.createElement('td');
    valueCell.className = 'epx-dmn-expr__cell';
    if (!bind.bindingFormula) bind.bindingFormula = createExpression(ctx.moddle, 'dmn:LiteralExpression');
    valueCell.appendChild(ctx.render(bind.bindingFormula, ctx));
    tr.appendChild(valueCell);

    const typeCell = document.createElement('td');
    typeCell.className = 'epx-dmn-expr__type';
    typeCell.appendChild(buildTypePicker(bind.bindingFormula, ctx, (newExpr) => {
      bind.bindingFormula = newExpr;
      ctx.onChange();
    }));
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove binding';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.binding.splice(idx, 1);
      ctx.onChange();
    });
    typeCell.appendChild(rm);
    tr.appendChild(typeCell);

    table.appendChild(tr);
  });

  const addTr = document.createElement('tr');
  const addTd = document.createElement('td');
  addTd.colSpan = 3;
  addTd.className = 'epx-dmn-expr__add-row';
  addTd.textContent = '+ Add parameter binding';
  addTd.addEventListener('click', () => {
    expr.binding.push(createBinding(ctx.moddle, { name: '' }));
    ctx.onChange();
  });
  addTr.appendChild(addTd);
  table.appendChild(addTr);

  box.appendChild(table);
  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// Relation — table of named columns × rows-of-literal-values.
// ──────────────────────────────────────────────────────────────────────────
export function renderRelation(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--relation';

  if (!expr.column) expr.column = [];
  if (!expr.row) expr.row = [];

  const table = document.createElement('table');
  table.className = 'epx-dmn-expr__grid epx-dmn-expr__relation';

  // Header row: column names
  const thead = document.createElement('thead');
  const headerTr = document.createElement('tr');
  const cornerTh = document.createElement('th');
  cornerTh.className = 'epx-dmn-expr__relation-corner';
  cornerTh.textContent = '#';
  headerTr.appendChild(cornerTh);
  expr.column.forEach((col, cIdx) => {
    const th = document.createElement('th');
    th.className = 'epx-dmn-expr__relation-head';
    th.appendChild(makeEditable(col.name || '', 'column', (v) => {
      col.name = v;
      ctx.onChange();
    }));
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove column';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.column.splice(cIdx, 1);
      expr.row.forEach((r) => { if (r.elements && r.elements.length > cIdx) r.elements.splice(cIdx, 1); });
      ctx.onChange();
    });
    th.appendChild(rm);
    headerTr.appendChild(th);
  });
  const addColTh = document.createElement('th');
  addColTh.className = 'epx-dmn-expr__add-col';
  addColTh.textContent = '+';
  addColTh.title = 'Add column';
  addColTh.addEventListener('click', () => {
    expr.column.push(createInformationItem(ctx.moddle, { name: '' }));
    expr.row.forEach((r) => {
      if (!r.elements) r.elements = [];
      r.elements.push(createExpression(ctx.moddle, 'dmn:LiteralExpression'));
    });
    ctx.onChange();
  });
  headerTr.appendChild(addColTh);
  thead.appendChild(headerTr);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  expr.row.forEach((row, rIdx) => {
    const tr = document.createElement('tr');

    const idx = document.createElement('td');
    idx.className = 'epx-dmn-expr__index';
    idx.textContent = String(rIdx + 1);
    idx.title = 'Remove row';
    idx.addEventListener('click', () => {
      expr.row.splice(rIdx, 1);
      ctx.onChange();
    });
    tr.appendChild(idx);

    if (!row.elements) row.elements = [];
    expr.column.forEach((_col, cIdx) => {
      const cellExpr = row.elements[cIdx];
      const td = document.createElement('td');
      td.className = 'epx-dmn-expr__cell';
      if (!cellExpr) {
        const fresh = createExpression(ctx.moddle, 'dmn:LiteralExpression');
        row.elements[cIdx] = fresh;
        td.appendChild(makeFeelEditable('', '', (v) => {
          fresh.text = v;
          ctx.onChange();
        }, ctx.getScope));
      } else if (cellExpr.$type === 'dmn:LiteralExpression') {
        td.appendChild(makeFeelEditable(textOf(cellExpr), '', (v) => {
          cellExpr.text = v;
          ctx.onChange();
        }, ctx.getScope));
      } else {
        // Uncommon for relations; fall back to nested rendering.
        td.appendChild(ctx.render(cellExpr, ctx));
      }
      tr.appendChild(td);
    });
    // Trailing empty cell aligned with the add-col header
    const tailTd = document.createElement('td');
    tailTd.className = 'epx-dmn-expr__relation-tail';
    tr.appendChild(tailTd);

    tbody.appendChild(tr);
  });

  // Add-row
  const addRowTr = document.createElement('tr');
  const addRowTd = document.createElement('td');
  addRowTd.colSpan = expr.column.length + 2;
  addRowTd.className = 'epx-dmn-expr__add-row';
  addRowTd.textContent = '+ Add row';
  addRowTd.addEventListener('click', () => {
    const newRow = ctx.moddle.create('dmn:List', {
      elements: expr.column.map(() => createExpression(ctx.moddle, 'dmn:LiteralExpression')),
    });
    expr.row.push(newRow);
    ctx.onChange();
  });
  addRowTr.appendChild(addRowTd);
  tbody.appendChild(addRowTr);

  table.appendChild(tbody);
  box.appendChild(table);
  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// FunctionDefinition — parameter list + body expression.
// ──────────────────────────────────────────────────────────────────────────
export function renderFunctionDefinition(expr, ctx) {
  const box = document.createElement('div');
  box.className = 'epx-dmn-expr__box epx-dmn-expr__box--function';

  if (!expr.formalParameter) expr.formalParameter = [];

  const paramsRow = document.createElement('div');
  paramsRow.className = 'epx-dmn-expr__fn-params';
  const paramsLabel = document.createElement('span');
  paramsLabel.className = 'epx-dmn-expr__fn-label';
  paramsLabel.textContent = `F (${expr.kind || 'FEEL'})`;
  paramsRow.appendChild(paramsLabel);

  expr.formalParameter.forEach((p, pIdx) => {
    const chip = document.createElement('span');
    chip.className = 'epx-dmn-expr__fn-param';
    chip.appendChild(bindNoSpaces(makeEditable(p.name || '', 'param', (v) => {
      p.name = sanitizeFeelIdent(v);
      ctx.onChange();
    })));
    const colon = document.createElement('span');
    colon.className = 'epx-dmn-expr__fn-typeref';
    colon.textContent = ':';
    chip.appendChild(colon);
    const typeSel = document.createElement('select');
    typeSel.className = 'epx-dmn-expr__fn-typeselect';
    ['string', 'number', 'boolean', 'date', 'time', 'date and time', 'duration', 'any'].forEach((t) => {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = t;
      if (t === (p.typeRef || 'string')) opt.selected = true;
      typeSel.appendChild(opt);
    });
    typeSel.addEventListener('change', () => {
      p.typeRef = typeSel.value || null;
      ctx.onChange();
    });
    chip.appendChild(typeSel);
    const rm = document.createElement('button');
    rm.type = 'button';
    rm.className = 'epx-dmn-expr__row-remove';
    rm.title = 'Remove parameter';
    rm.textContent = '×';
    rm.addEventListener('click', () => {
      expr.formalParameter.splice(pIdx, 1);
      ctx.onChange();
    });
    chip.appendChild(rm);
    paramsRow.appendChild(chip);
  });

  const addParam = document.createElement('button');
  addParam.type = 'button';
  addParam.className = 'epx-dmn-expr__fn-add';
  addParam.textContent = '+ param';
  addParam.addEventListener('click', () => {
    expr.formalParameter.push(createInformationItem(ctx.moddle, { name: '', typeRef: 'string' }));
    ctx.onChange();
  });
  paramsRow.appendChild(addParam);
  box.appendChild(paramsRow);

  const bodyWrap = document.createElement('div');
  bodyWrap.className = 'epx-dmn-expr__fn-body';
  if (!expr.body) expr.body = createExpression(ctx.moddle, 'dmn:LiteralExpression');

  // Formal parameters are bound inside the body — surface them in the local
  // scope so FEEL fields recognize them as known.
  const paramScope = expr.formalParameter
    .filter((p) => p && p.name)
    .map((p) => ({
      kind: 'param',
      name: p.name,
      typeRef: p.typeRef || null,
      sourceId: p.id || null,
      sourceName: 'Parameter',
      sourceKind: 'Parameter',
    }));
  const baseGetScope = typeof ctx.getScope === 'function' ? ctx.getScope : () => [];
  const bodyCtx = paramScope.length
    ? { ...ctx, getScope: () => [...baseGetScope(), ...paramScope] }
    : ctx;

  bodyWrap.appendChild(ctx.render(expr.body, bodyCtx));
  const typeRow = document.createElement('div');
  typeRow.className = 'epx-dmn-expr__fn-body-type';
  typeRow.appendChild(buildTypePicker(expr.body, ctx, (newExpr) => {
    expr.body = newExpr;
    ctx.onChange();
  }));
  bodyWrap.appendChild(typeRow);
  box.appendChild(bodyWrap);

  return box;
}

// ──────────────────────────────────────────────────────────────────────────
// Type picker — a tiny dropdown next to each cell that lets the user
// convert the expression to a different boxed type. Writes the new moddle
// element back via `commit(newExpr)` and triggers onChange/redraw.
// ──────────────────────────────────────────────────────────────────────────
function buildTypePicker(current, ctx, commit) {
  const wrap = document.createElement('span');
  wrap.className = 'epx-dmn-expr__type-picker';
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'epx-dmn-expr__type-btn';
  btn.title = 'Change expression type';
  btn.textContent = getExpressionTypeLabel(current && current.$type);
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    openTypeMenu(e.currentTarget, current && current.$type, (newType) => {
      if (!newType || newType === (current && current.$type)) return;
      const fresh = createExpression(ctx.moddle, newType);
      commit(fresh);
    });
  });
  wrap.appendChild(btn);
  return wrap;
}

function openTypeMenu(anchor, currentType, pick) {
  // Close any existing menus
  document.querySelectorAll('.epx-dmn-expr__type-menu').forEach((m) => m.remove());
  const menu = document.createElement('div');
  menu.className = 'epx-dmn-expr__type-menu';
  const rect = anchor.getBoundingClientRect();
  menu.style.left = rect.left + 'px';
  menu.style.top = (rect.bottom + 2) + 'px';
  // Avoid circular import — keep the list inline. Entries with a leading
  // '_separator' type render as a non-clickable group label so EPX extensions
  // are visually segregated from the standard DMN boxed-expression types.
  [
    { type: 'dmn:LiteralExpression', label: 'Literal Expression' },
    { type: 'dmn:Context',           label: 'Context' },
    { type: 'dmn:List',              label: 'List' },
    { type: 'dmn:Invocation',        label: 'Invocation' },
    { type: 'dmn:Relation',          label: 'Relation' },
    { type: 'dmn:FunctionDefinition',label: 'Function Definition' },
    { type: '_separator',            label: 'Extensiones' },
    { type: 'epx:MachineLearningDecision', label: '🧠 Machine Learning' },
  ].forEach((opt) => {
    if (opt.type === '_separator') {
      const sep = document.createElement('div');
      sep.className = 'epx-dmn-expr__type-menu-sep';
      sep.textContent = opt.label;
      menu.appendChild(sep);
      return;
    }
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'epx-dmn-expr__type-menu-item';
    if (opt.type === currentType) b.classList.add('is-active');
    b.textContent = opt.label;
    b.addEventListener('click', () => {
      menu.remove();
      pick(opt.type);
    });
    menu.appendChild(b);
  });
  document.body.appendChild(menu);
  const close = (e) => {
    if (!menu.contains(e.target) && e.target !== anchor) {
      menu.remove();
      document.removeEventListener('mousedown', close);
    }
  };
  setTimeout(() => document.addEventListener('mousedown', close), 0);
}
